import React, { useCallback, useEffect, useState } from 'react';

const COUNTRIES = [
  { code: 'TR', name: 'Turkey' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'BR', name: 'Brazil' },
  { code: 'IN', name: 'India' },
  { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'PL', name: 'Poland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'GR', name: 'Greece' },
  { code: 'AR', name: 'Argentina' },
  { code: 'ZA', name: 'South Africa' },
];

const CATEGORIES = [
  { id: 67, name: 'Shopping' },
  { id: 0, name: 'All categories' },
  { id: 3, name: 'Arts & Entertainment' },
  { id: 5, name: 'Autos & Vehicles' },
  { id: 7, name: 'Beauty & Fitness' },
  { id: 10, name: 'Computers & Electronics' },
  { id: 12, name: 'Finance' },
  { id: 13, name: 'Food & Drink' },
  { id: 14, name: 'Games' },
  { id: 16, name: 'Health' },
  { id: 19, name: 'Home & Garden' },
  { id: 20, name: 'Internet & Telecom' },
  { id: 22, name: 'Jobs & Education' },
  { id: 34, name: 'Online Communities' },
  { id: 49, name: 'Real Estate' },
  { id: 66, name: 'Science' },
  { id: 71, name: 'Sports' },
  { id: 174, name: 'Travel' },
];

function unwrap(val) {
  if (val === null || val === undefined) return val;
  if (typeof val === 'object' && Object.prototype.hasOwnProperty.call(val, 'value')) return val.value;
  if (typeof val === 'object') { try { return JSON.stringify(val); } catch { return String(val); } }
  return val;
}

function normalizeBq(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(row => {
    const n = {};
    Object.entries(row || {}).forEach(([k, v]) => { n[k] = unwrap(v); });
    return n;
  });
}

const TIME_RANGES = [
  { value: '1d', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '12m', label: 'Last 12 months' },
];

export default function GoogleTrendsCategoryTest() {
  const [geo, setGeo] = useState('TR');
  const [categoryId, setCategoryId] = useState(67);
  const [timeRange, setTimeRange] = useState('1d');

  const [topQueries, setTopQueries] = useState([]);
  const [risingQueries, setRisingQueries] = useState([]);
  const [meta, setMeta] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [loadingCat, setLoadingCat] = useState(false);
  const [errCat, setErrCat] = useState('');

  const [bqDailyTop, setBqDailyTop] = useState([]);
  const [bqRising, setBqRising] = useState([]);
  const [loadingBq, setLoadingBq] = useState({ daily: false, rising: false });

  const selectedCat = CATEGORIES.find(c => c.id === categoryId) || CATEGORIES[0];
  const countryName = COUNTRIES.find(c => c.code === geo)?.name || geo;

  const fetchCategoryDiscover = useCallback(async (country, catId, time, forceRefresh = false) => {
    setLoadingCat(true);
    setErrCat('');
    setMeta(null);
    setFromCache(false);
    try {
      const timeQ = time ? `&time=${encodeURIComponent(time)}` : '';
      const noCacheQ = forceRefresh ? '&noCache=1' : '';
      const res = await fetch(`/api/google-trends/category-discover?geo=${encodeURIComponent(country)}&category=${catId}${timeQ}${noCacheQ}`, { credentials: 'include' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed');
      setTopQueries(data.top || []);
      setRisingQueries(data.rising || []);
      setMeta(data.meta || null);
      setFromCache(!!data.fromCache);
    } catch (e) {
      setErrCat(e?.message || 'Failed');
      setTopQueries([]);
      setRisingQueries([]);
    } finally {
      setLoadingCat(false);
    }
  }, []);

  const fetchBqDaily = useCallback(async (country) => {
    setLoadingBq(l => ({ ...l, daily: true }));
    try {
      const res = await fetch(`/api/google-trends/international/daily/top?country=${encodeURIComponent(country)}`, { credentials: 'include' });
      const data = await res.json();
      if (data.ok) setBqDailyTop(normalizeBq(data.rows || []));
    } catch { /* ignore */ }
    finally { setLoadingBq(l => ({ ...l, daily: false })); }
  }, []);

  const fetchBqRising = useCallback(async (country) => {
    setLoadingBq(l => ({ ...l, rising: true }));
    try {
      const res = await fetch(`/api/google-trends/international/top-rising?country=${encodeURIComponent(country)}&limit=100`, { credentials: 'include' });
      const data = await res.json();
      if (data.ok) setBqRising(normalizeBq(data.rows || []));
    } catch { /* ignore */ }
    finally { setLoadingBq(l => ({ ...l, rising: false })); }
  }, []);

  useEffect(() => {
    fetchBqDaily(geo);
    fetchBqRising(geo);
  }, [geo, fetchBqDaily, fetchBqRising]);

  useEffect(() => {
    fetchCategoryDiscover(geo, categoryId, timeRange);
  }, [geo, categoryId, timeRange, fetchCategoryDiscover]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-5">

        {/* Header */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl font-bold">Google Trends Explorer</h1>
              <p className="text-sm text-blue-100 mt-0.5">Kategori bazlı trend kelimeleri keşfet</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={geo}
                onChange={e => setGeo(e.target.value)}
                className="px-3 py-2 text-sm border-0 rounded-lg bg-white/20 text-white backdrop-blur focus:outline-none focus:ring-2 focus:ring-white/40 [&>option]:text-gray-900"
              >
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
              </select>
              <select
                value={categoryId}
                onChange={e => setCategoryId(Number(e.target.value))}
                className="px-3 py-2 text-sm border-0 rounded-lg bg-white/20 text-white backdrop-blur focus:outline-none focus:ring-2 focus:ring-white/40 [&>option]:text-gray-900"
              >
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select
                value={timeRange}
                onChange={e => setTimeRange(e.target.value)}
                className="px-3 py-2 text-sm border-0 rounded-lg bg-white/20 text-white backdrop-blur focus:outline-none focus:ring-2 focus:ring-white/40 [&>option]:text-gray-900"
              >
                {TIME_RANGES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Category Discover */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-purple-50 border-b border-purple-100 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-sm font-bold text-purple-800">
                {selectedCat.name} — {countryName}
              </h2>
              <p className="text-xs text-purple-600 mt-0.5">
                {loadingCat
                  ? 'Seed keyword\'lerle taranıyor… (bu biraz sürebilir)'
                  : meta
                    ? `${meta.totalAvailable} seed'den ${meta.totalSeeds} seçildi → ${meta.success} başarılı, ${meta.skipped} atlandı → ${topQueries.length} top + ${risingQueries.length} rising keyword`
                    : 'Henüz veri yok'}
                {!loadingCat && timeRange && <span className="ml-1">({TIME_RANGES.find(t => t.value === timeRange)?.label})</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {fromCache && (
                <span className="text-[10px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">cached</span>
              )}
              {meta && meta.skipped > 0 && (
                <span className="text-[10px] bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">{meta.success}/{meta.totalSeeds} seed OK</span>
              )}
              {!loadingCat && (
                <button
                  onClick={() => fetchCategoryDiscover(geo, categoryId, timeRange, true)}
                  className="text-[10px] bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full hover:bg-purple-300 transition-colors"
                >
                  Yenile
                </button>
              )}
            </div>
          </div>
          {errCat && <div className="px-4 py-2 text-xs text-red-600 bg-red-50">{errCat}</div>}
          {loadingCat && (
            <div className="px-5 py-10 text-center">
              <div className="inline-block animate-spin w-6 h-6 border-2 border-purple-300 border-t-purple-600 rounded-full mb-3" />
              <div className="text-sm text-gray-500">Taranıyor… seed keyword'ler paralel olarak çekiliyor</div>
              <div className="text-xs text-gray-400 mt-1">Bu işlem 20-40 saniye sürebilir (seed sayısına göre)</div>
            </div>
          )}
          {!loadingCat && (
            <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
              {/* TOP */}
              <div>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Top Queries <span className="text-gray-400 font-normal">({topQueries.length})</span>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10">#</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Query</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-16">Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {topQueries.map((q, i) => (
                        <tr key={i} className="hover:bg-purple-50/30">
                          <td className="px-4 py-1.5 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-4 py-1.5 text-gray-900 font-medium">{q.query}</td>
                          <td className="px-4 py-1.5 text-right text-gray-600">{q.value}</td>
                        </tr>
                      ))}
                      {topQueries.length === 0 && (
                        <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* RISING */}
              <div>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Rising Queries <span className="text-gray-400 font-normal">({risingQueries.length})</span>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10">#</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Query</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-28">% Growth</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {risingQueries.map((q, i) => (
                        <tr key={i} className="hover:bg-purple-50/30">
                          <td className="px-4 py-1.5 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-4 py-1.5 text-gray-900 font-medium">{q.query}</td>
                          <td className="px-4 py-1.5 text-right text-emerald-600 font-semibold">
                            {q.value === 'Breakout' ? 'Breakout' : `+${Number(q.value).toLocaleString()}%`}
                          </td>
                        </tr>
                      ))}
                      {risingQueries.length === 0 && (
                        <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* BigQuery — overall */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-emerald-800">BigQuery — Daily Top (genel)</h2>
                <p className="text-xs text-emerald-600 mt-0.5">Kategorisiz, genel top terimler</p>
              </div>
              <span className="text-xs text-emerald-500 bg-emerald-100 px-2 py-0.5 rounded-full font-medium">{bqDailyTop.length}</span>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10">#</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Term</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-20">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loadingBq.daily ? (
                    <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                  ) : bqDailyTop.map((r, i) => (
                    <tr key={i} className="hover:bg-blue-50/30">
                      <td className="px-4 py-1.5 text-xs text-gray-400">{i + 1}</td>
                      <td className="px-4 py-1.5 text-gray-900 font-medium">{r.term}</td>
                      <td className="px-4 py-1.5 text-right text-gray-600">{r.total_score}</td>
                    </tr>
                  ))}
                  {!loadingBq.daily && bqDailyTop.length === 0 && (
                    <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-orange-50 border-b border-orange-100 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-orange-800">BigQuery — Rising (genel)</h2>
                <p className="text-xs text-orange-600 mt-0.5">Kategorisiz, hızlı yükselen terimler</p>
              </div>
              <span className="text-xs text-orange-500 bg-orange-100 px-2 py-0.5 rounded-full font-medium">{bqRising.length}</span>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10">#</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Term</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-24">% Gain</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-16">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loadingBq.rising ? (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                  ) : bqRising.map((r, i) => (
                    <tr key={i} className="hover:bg-blue-50/30">
                      <td className="px-4 py-1.5 text-xs text-gray-400">{r.rank}</td>
                      <td className="px-4 py-1.5 text-gray-900 font-medium">{r.term}</td>
                      <td className="px-4 py-1.5 text-right text-emerald-600 font-semibold">
                        {r.percent_gain != null ? `+${Number(r.percent_gain).toLocaleString()}%` : '—'}
                      </td>
                      <td className="px-4 py-1.5 text-right text-gray-600">{r.score}</td>
                    </tr>
                  ))}
                  {!loadingBq.rising && bqRising.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
